/**
 * Dinheiro entra como a pessoa digita e sai em centavos.
 *
 * Mesma ideia do `normalizePhone`: o barbeiro escreve do jeito dele e o
 * sistema guarda de um jeito só. Centavos inteiros, nunca ponto flutuante —
 * `0.1 + 0.2` não é `0.3`, e um caixa que erra um centavo por atendimento
 * perde a confiança do dono em um mês.
 */

/** Teto de sanidade: um corte de dez mil reais é dedo escorregando, não preço. */
export const MAX_PRICE_CENTS = 100_000_00;

/**
 * "70,00", "70.00", "R$ 70" ou "70" → 7000.
 *
 * Aceita vírgula e ponto porque o teclado do iPhone oferece um ou outro
 * conforme o idioma do aparelho, e ninguém deveria ter que saber disso.
 *
 * Devolve `null` para o que não dá para ler — inclusive o campo vazio, que não
 * é zero: é "não mexi nisso", e quem chamou decide o que fazer com a diferença.
 */
export function parseMoneyToCents(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  // Tira tudo que não é dígito ou separador decimal. O separador de milhar
  // some junto: "1.200,50" e "1200,50" são o mesmo número dito de dois jeitos.
  const cleaned = trimmed
    .replace(/[^\d,.]/g, '')
    .replace(/\.(?=\d{3}\b)/g, '')
    .replace(',', '.');

  if (cleaned === '' || cleaned === '.') return null;

  const parsed = Number.parseFloat(cleaned);
  if (!Number.isFinite(parsed) || parsed < 0) return null;

  const cents = Math.round(parsed * 100);
  return cents > MAX_PRICE_CENTS ? null : cents;
}

/** Centavos no formato que um campo de edição mostra: "70,00". */
export function formatMoneyInput(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}
